import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { Alert } from '@components/Alert';
import { Dialog } from '@components/dialogs/Dialog';
import { Button } from '@components/dialogs/Dialog/components/Button';
import { ROUTES } from '@routes/routes';
import { useTranslation } from 'react-i18next';
import { Image } from '@components/Image';
import { formatLocaleDateShort } from '@helpers/dateHelpers';
import { SubmissionStatus } from '@api/competitionsApi/interfaces';
import { WeaponGroupChips } from '@components/competitions/WeaponGroupChips';
import { IViewScorecardDialog } from './interfaces';

const SCORECARD_DIALOG_WIDTH = 'clamp(320px, calc(100vw - 32px), 720px)';

const statusColors: Record<
  SubmissionStatus,
  'success' | 'error' | 'warning'
> = {
  approved: 'success',
  rejected: 'error',
  pending: 'warning',
};

export const ViewScorecardDialog: React.FC<IViewScorecardDialog> = ({
  show,
  imageId,
  value,
  shotOn,
  weaponGroup,
  note,
  memberName,
  reviewNote,
  status,
  canReview = false,
  isReviewing = false,
  onApprove,
  onReject,
  onDelete,
  onClose,
}): JSX.Element | null => {
  const { t } = useTranslation();

  if (!imageId) {
    return null;
  }

  const showReviewNote = status === 'rejected' && reviewNote;
  const showMemberNote = Boolean(note?.trim());
  const showReviewActions =
    canReview && status === 'pending' && (onApprove || onReject);
  const showDeleteAction =
    !canReview && status === 'pending' && Boolean(onDelete);
  const showDetails =
    memberName ||
    value !== undefined ||
    shotOn ||
    weaponGroup ||
    status ||
    showMemberNote;

  return (
    <Dialog
      show={show}
      title={t('competitions:portal.yourScores.viewSubmission')}
      onClose={onClose}
      minWidth={SCORECARD_DIALOG_WIDTH}
      maxWidth={false}
      buttons={
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
            }}
          >
            {showDeleteAction && onDelete && (
              <Button
                label={t('competitions:portal.yourScores.delete.button')}
                onClick={onDelete}
                variant="danger"
              />
            )}
            {showReviewActions && onReject && (
              <Button
                label={t(
                  'competitions:manage.details.submissions.actions.reject'
                )}
                onClick={onReject}
                disabled={isReviewing}
                variant="danger"
              />
            )}
            {showReviewActions && onApprove && (
              <Button
                label={t(
                  'competitions:manage.details.submissions.actions.approve'
                )}
                onClick={onApprove}
                disabled={isReviewing}
                variant="primary"
              />
            )}
          </Box>
          <Box sx={{ marginLeft: 'auto' }}>
            <Button label={t('buttons.close')} onClick={onClose} />
          </Box>
        </Box>
      }
    >
      {showDetails && (
        <Box
          sx={{
            mb: 2,
          }}
        >
          {memberName && (
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{
                mb: 1.5,
              }}
            >
              {memberName}
            </Typography>
          )}

          {(value !== undefined || shotOn || weaponGroup || status) && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                gap: 3,
                mb: showMemberNote ? 2 : 0,
              }}
            >
              {value !== undefined && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('competitions:portal.yourScores.columns.value')}
                  </Typography>
                  <Typography variant="body1">{value}</Typography>
                </Box>
              )}
              {shotOn && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('competitions:portal.yourScores.columns.shotOn')}
                  </Typography>
                  <Typography variant="body1">
                    {formatLocaleDateShort(shotOn)}
                  </Typography>
                </Box>
              )}
              {weaponGroup && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('competitions:portal.yourScores.columns.weaponGroup')}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.5,
                    }}
                  >
                    <WeaponGroupChips weaponGroups={[weaponGroup]} />
                  </Box>
                </Box>
              )}
              {status && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('competitions:portal.yourScores.columns.status')}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.5,
                    }}
                  >
                    <Chip
                      label={t(
                        `competitions:portal.yourScores.status.${status}`
                      )}
                      variant="outlined"
                      size="small"
                      color={statusColors[status]}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {showMemberNote && (
            <Alert
              severity="info"
              title={t('competitions:portal.dialogs.submitScore.fields.note')}
              content={note}
            />
          )}
        </Box>
      )}

      {showReviewNote && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          title={t('competitions:portal.yourScores.reviewNote')}
          content={reviewNote}
        />
      )}

      <Typography
        variant="body2"
        fontWeight="bold"
        color="text.secondary"
        sx={{
          mb: 1,
        }}
      >
        {t('competitions:portal.dialogs.submitScore.fields.scorecard')}
      </Typography>
      <Image
        imageSrc={ROUTES.images.image(imageId)}
        height="70vh"
        borderRadius
        sx={{
          mt: 0,
          maxWidth: '100%',
          '& img': {
            objectFit: 'contain',
          },
        }}
      />
    </Dialog>
  );
};
